import React, { useState, useEffect, useRef } from "react";
import { createJob, updateJob, getJob, upsertLocationFromStop, getCompany, logAudit, findJobByTrackingNumber } from "../../services/firestore";
import { statusAdvanceFromSignatures } from "../../services/statusAdvance";
import { uploadGuardPhoto } from "../../services/storage";
import { todayYmd } from "../../services/billingReadiness";
import { resolveJobType, buildFerrariLocalStop } from "../../services/jobDirection";
import { mergeGroupCards } from "../../services/ferrariPodGrouping";
import { ferrariParseWarnings, scheduledDateWarning } from "../../services/parseChecks";
import { findLocationMatch } from "../../services/locationIdentity";
import { useAuth } from "../../contexts/AuthContext";
import { collection, getDocs, query, where } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../firebase";
import { useEscapeKey } from "../../hooks/useEscapeKey";
import PlacesAutocomplete from "../PlacesAutocomplete";
import { fileToBase64, downscaleImage, isLikelySameContact } from "./JobCreateModal";

// Bulk Ferrari POD/HAWB upload. N form images in one batch, each parsed
// in ISOLATION (one parseEmail call per image — multiple forms in a single
// call cross-contaminate fields), then the owner groups the resulting cards
// into jobs before hitting Create. Same-group cards merge: stops union
// (dedup by locationId/address), packages union (each POD image stays its
// own package row with its own HAWB + photo), contact from the signed /
// most-complete card wins.

function uid() { return Math.random().toString(36).slice(2, 9); }

const MAX_IMAGES = 15;

// Pull the JSON object out of a parseEmail response (same post-processing
// as JobCreateModal's single-parse path: concat text blocks, strip fences,
// slice to the outermost braces).
function extractParsedJson(data) {
  const textBlocks = (data.content || []).filter((b) => b.type === "text").map((b) => b.text).join("\n");
  let text = textBlocks.replace(/^```(?:json)?\s*/i, "").replace(/\s*```\s*$/i, "").trim();
  if (!text) throw new Error("Model returned an empty response.");
  if (!text.startsWith("{")) {
    const first = text.indexOf("{");
    const last = text.lastIndexOf("}");
    if (first >= 0 && last > first) text = text.slice(first, last + 1);
  }
  return JSON.parse(text);
}

export default function FerrariBulkUploadModal({ onClose, onCreated, companyId }) {
  const { userProfile } = useAuth();
  // [{ id, file, name, preview }] — images only; PODs are photographs and
  // the POD panel/PDF render images, not PDFs.
  const [images, setImages] = useState([]);
  // One card per image once parsing starts. Each card:
  //   { id, imageId, status: "parsing"|"done"|"error", error,
  //     groupId, jobType, dirSource, needsReview, isFerrari, extracted,
  //     trackingNumber, stop, matchedContacts, contactOnFile,
  //     packages: [{hawbNumber, description, declaredValue, weight, pieces}],
  //     scheduledDate, scheduledTime, guards, signed }
  const [cards, setCards] = useState([]);
  const [step, setStep] = useState(1);               // 1 = upload, 2 = group & create
  const [parsing, setParsing] = useState(false);
  const [selected, setSelected] = useState(new Set()); // card ids picked for a merge
  const [markComplete, setMarkComplete] = useState(true);
  const [creating, setCreating] = useState(false);
  const [createProgress, setCreateProgress] = useState("");
  const [error, setError] = useState("");
  const [brokers, setBrokers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [companyDoc, setCompanyDoc] = useState(null);
  const fileInputRef = useRef();
  const [dragOver, setDragOver] = useState(false);
  const dragCardId = useRef(null);
  // Full-size look at a scanned form. The 56px card thumbnail is too small
  // to check what the reader pulled off it — which is exactly what the owner
  // is doing on this screen.
  const [zoomImage, setZoomImage] = useState(null);

  useEscapeKey(() => {
    // Escape belongs to the open viewer first — otherwise it would discard
    // the whole batch behind it.
    if (zoomImage) { setZoomImage(null); return; }
    if (creating) return; // don't abandon mid-create
    if (images.length > 0 && !window.confirm("Discard this bulk upload?")) return;
    onClose?.();
  });

  useEffect(() => {
    async function load() {
      const [bSnap, lSnap, company] = await Promise.all([
        getDocs(query(collection(db, "brokers"), where("companyId", "==", companyId))),
        getDocs(query(collection(db, "locations"), where("companyId", "==", companyId))),
        getCompany(companyId),
      ]);
      setBrokers(bSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLocations(lSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setCompanyDoc(company);
    }
    if (companyId) load();
  }, [companyId]);

  const ferrariBroker = brokers.find((b) => (b.name || "").toLowerCase().includes("ferrari")) || null;

  // ── Step 1: collect images ───────────────────────────────
  async function addFiles(files) {
    const imgs = Array.from(files || []).filter((f) => f.type?.startsWith("image/"));
    if (!imgs.length) {
      if (files?.length) setError("Only images can be bulk-parsed — drop photos or screenshots of the POD forms.");
      return;
    }
    setError("");
    const room = MAX_IMAGES - images.length;
    if (imgs.length > room) setError(`Max ${MAX_IMAGES} images per batch — kept the first ${room}.`);
    const processed = await Promise.all(imgs.slice(0, Math.max(0, room)).map(downscaleImage));
    setImages((prev) => [...prev, ...processed.map((file) => ({
      id: uid(), file, name: file.name || "", preview: URL.createObjectURL(file),
    }))]);
  }
  function removeImage(id) {
    setImages((prev) => {
      const gone = prev.find((p) => p.id === id);
      if (gone?.preview) URL.revokeObjectURL(gone.preview);
      return prev.filter((p) => p.id !== id);
    });
  }

  // ── Parse: one callable invocation per image, in parallel ─
  // Build a preview card from one parsed form. Direction comes from
  // resolveJobType (signature/dest-airport cascade — never the AI's guess
  // alone), the local stop from buildFerrariLocalStop, and the airport is
  // ALWAYS the company hub (the form's DEST. AIRPORT is Ferrari's
  // final-mile destination, not where this courier drives).
  function buildCardFields(parsed) {
    const extracted = parsed.extracted || null;
    const shape = extracted?.documentShape || null;
    const isFerrari = shape === "ferrari_waybill" || shape === "ferrari_export_letter";
    const dir = resolveJobType(extracted || {}, companyDoc || {}, {
      statedDirection: extracted?.statedDirection,
      aiSuggestedJobType: parsed.jobType,
    });
    const jobType = dir.jobType === "inbound" ? "inbound" : "outbound";
    const trackingNumber = extracted?.trackingNumber || parsed.jobNumber || "";

    let stop = isFerrari
      ? buildFerrariLocalStop(extracted, jobType)
      : (parsed.stops || []).find((s) => s.type !== "airport") || null;
    let matchedContacts = null;
    if (stop) {
      const { match, confidence, reason } = findLocationMatch(locations, {
        name: stop.locationName, address: stop.address, lat: stop.lat, lng: stop.lng, placeId: stop.placeId,
      });
      // An UNCERTAIN match — same coordinates, same address, or same name —
      // used to be dropped on the floor here, so a scan that read the
      // address a little differently quietly became a second copy of a
      // place already on file, losing its saved coords and contacts. Carry
      // it as a suggestion the owner confirms, exactly as job creation does.
      if (match && (confidence === "strong" || confidence === "weak")) {
        stop = {
          ...stop,
          suggestedMatch: {
            id: match.id,
            name: match.name || "",
            address: match.address || "",
            confidence,
            reason: reason || "",
          },
        };
      }
      if (match && confidence === "exact") {
        stop = {
          ...stop,
          locationId: match.id,
          locationName: match.name || stop.locationName || "",
          address: match.address || stop.address || "",
          lat: typeof match.lat === "number" ? match.lat : stop.lat ?? null,
          lng: typeof match.lng === "number" ? match.lng : stop.lng ?? null,
        };
        matchedContacts = Array.isArray(match.contacts) ? match.contacts : [];
      }
    }
    const contactOnFile = !!(stop && matchedContacts && isLikelySameContact(
      { name: stop.contactName, phone: stop.contactPhone }, matchedContacts));

    // Packages — each POD form usually carries one order. Blank HAWB falls
    // back to the form's tracking number so the row is still identifiable.
    let packages = (parsed.packages || []).map((p) => ({
      hawbNumber: p.hawbNumber || trackingNumber || "",
      description: p.description || "",
      declaredValue: p.declaredValue || "",
      weight: p.weight || "",
      pieces: p.pieces != null ? p.pieces : "",
    }));
    if (!packages.length) {
      packages = [{ hawbNumber: trackingNumber || "", description: "", declaredValue: "", weight: "", pieces: "" }];
    }

    // Guard count: dispatcher's ask, bumped by the Ferrari 100-lb rule
    // (any single package over 100 lbs → two-person handling).
    const parsedGuards = Number(parsed.guardsRequired);
    let guards = (parsedGuards >= 1 && parsedGuards <= 4) ? parsedGuards : 1;
    const heavy = packages.some((p) => {
      const w = parseFloat(String(p.weight || "").replace(/[^0-9.]/g, ""));
      return Number.isFinite(w) && w > 100;
    });
    if (heavy) guards = Math.max(2, guards);

    // Form sanity checks — wrong document type, missing/odd tracking
    // number, package # disagreeing with the form's own tracking #.
    // Advisory: shown as an alert on the card, never blocks.
    const warnings = ferrariParseWarnings({
      documentShape: shape,
      trackingNumber: extracted?.trackingNumber,
      packages: parsed.packages || [],
      stop,
    });

    return {
      status: "done", error: "",
      warnings,
      isFerrari, extracted, trackingNumber,
      jobType, dirSource: dir.source, needsReview: dir.needsReview || (isFerrari && !stop),
      stop, contactOnFile, packages, guards,
      scheduledDate: parsed.scheduledDate || "",
      scheduledTime: parsed.scheduledTime || "",
      notes: parsed.notes || "",
      signed: {
        shipperSigned: !!extracted?.signaturesPresent?.shipperSigned,
        deliverySigned: !!extracted?.signaturesPresent?.deliveryConfirmationSigned,
      },
    };
  }

  async function parseOne(img) {
    const b64 = await fileToBase64(img.file);
    const fn = httpsCallable(functions, "parseEmail");
    const result = await fn({
      rawText: "",
      images: [{ mediaType: img.file.type || "image/jpeg", data: b64 }],
      hubAirport: companyDoc?.hubAirport || "",
    });
    const fields = buildCardFields(extractParsedJson(result.data));
    // Re-upload detection: a form whose tracking number matches an
    // existing job is a signed copy coming back — that card UPDATES the
    // existing job (status advance + signed POD) instead of creating a
    // duplicate. Lookup failure just means the card creates as normal.
    if (fields.trackingNumber) {
      try {
        const match = await findJobByTrackingNumber(companyId, fields.trackingNumber);
        if (match) {
          fields.existing = {
            id: match.id,
            jobNumber: match.jobNumber || fields.trackingNumber,
            status: match.status || "",
            completedAt: match.completedAt || null,
            stops: match.stops || [],
            packages: match.packages || [],
          };
        }
      } catch (err) {
        console.warn("Tracking-number lookup failed for", fields.trackingNumber, err);
      }
    }
    return fields;
  }

  async function handleParseAll() {
    if (!images.length || parsing) return;
    setParsing(true);
    setError("");
    const seeded = images.map((img) => ({ id: uid(), imageId: img.id, status: "parsing", error: "", groupId: null }));
    seeded.forEach((c) => { c.groupId = c.id; });
    setCards(seeded);
    setStep(2);
    await Promise.all(seeded.map(async (card) => {
      const img = images.find((i) => i.id === card.imageId);
      try {
        const fields = await parseOne(img);
        setCards((prev) => prev.map((c) => (c.id === card.id ? { ...c, ...fields } : c)));
      } catch (err) {
        console.error("Bulk POD parse failed for", img?.name, err);
        setCards((prev) => prev.map((c) => (c.id === card.id
          ? { ...c, status: "error", error: err?.message || "Parse failed" } : c)));
      }
    }));
    setParsing(false);
  }

  async function retryCard(cardId) {
    const card = cards.find((c) => c.id === cardId);
    const img = images.find((i) => i.id === card?.imageId);
    if (!card || !img) return;
    setCards((prev) => prev.map((c) => (c.id === cardId ? { ...c, status: "parsing", error: "" } : c)));
    try {
      const fields = await parseOne(img);
      setCards((prev) => prev.map((c) => (c.id === cardId ? { ...c, ...fields } : c)));
    } catch (err) {
      setCards((prev) => prev.map((c) => (c.id === cardId
        ? { ...c, status: "error", error: err?.message || "Parse failed" } : c)));
    }
  }

  function removeCard(cardId) {
    setCards((prev) => prev.filter((c) => c.id !== cardId));
    setSelected((prev) => { const n = new Set(prev); n.delete(cardId); return n; });
  }

  // ── Grouping ─────────────────────────────────────────────
  // Groups derived from cards[].groupId, ordered by SERVICE DATE — a batch
  // of a month's PODs comes off the scanner in whatever order the forms were
  // stacked, which is no order at all to check against a calendar. Earliest
  // first; a card whose date didn't parse sorts to the end, where it's
  // obvious it needs one. Ties keep upload order (sort is stable), so a
  // multi-card group stays put as the owner works through it.
  const groups = (() => {
    const byId = new Map();
    const seenAt = new Map();
    for (const c of cards) {
      if (c.status !== "done") continue;
      if (!byId.has(c.groupId)) { byId.set(c.groupId, []); seenAt.set(c.groupId, seenAt.size); }
      byId.get(c.groupId).push(c);
    }
    const dateOf = (members) => {
      const dates = members.map((m) => m.scheduledDate).filter(Boolean).sort();
      return dates[0] || "9999-99-99";
    };
    return [...byId.entries()]
      .map(([groupId, members]) => ({ groupId, members }))
      .sort((a, b) => {
        const d = dateOf(a.members).localeCompare(dateOf(b.members));
        return d !== 0 ? d : seenAt.get(a.groupId) - seenAt.get(b.groupId);
      });
  })();

  function toggleSelect(cardId) {
    // Update-cards (matched an existing job) can't be merged into a new
    // job — they patch the job they matched.
    if (cards.find((c) => c.id === cardId)?.existing) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(cardId)) next.delete(cardId);
      else next.add(cardId);
      return next;
    });
  }

  function mergeSelected() {
    const ids = [...selected];
    if (ids.length < 2) return;
    const target = cards.find((c) => c.id === ids[0])?.groupId;
    if (!target) return;
    setCards((prev) => prev.map((c) => (ids.includes(c.id) ? { ...c, groupId: target } : c)));
    setSelected(new Set());
  }

  function moveCardToGroup(cardId, groupId) {
    setCards((prev) => prev.map((c) => (c.id === cardId ? { ...c, groupId } : c)));
  }

  function popOut(cardId) {
    setCards((prev) => prev.map((c) => (c.id === cardId ? { ...c, groupId: c.id } : c)));
  }

  function updateCard(cardId, patch) {
    setCards((prev) => prev.map((c) => {
      if (c.id !== cardId) return c;
      const next = { ...c, ...patch };
      // Direction flip on a Ferrari card rebuilds the local stop from the
      // raw extracted blocks — inbound reads the consignee block, outbound
      // the pickup block. They're different addresses.
      if (patch.jobType && patch.jobType !== c.jobType && c.isFerrari && c.extracted) {
        next.stop = buildFerrariLocalStop(c.extracted, patch.jobType) || c.stop;
        next.dirSource = "manual";
        next.needsReview = false;
      }
      return next;
    }));
  }

  // ── Merge a group's cards into one job's stops + packages ─
  // Pure union/dedup rules live in ferrariPodGrouping.js (unit-tested).
  function buildGroupJob(members) {
    return mergeGroupCards(members, companyDoc || {});
  }

  // Groups that patch an existing job (signed re-uploads) vs. groups that
  // create a new one. Update-groups are always single-card (merging is
  // blocked on them).
  const updateGroups = groups.filter((g) => g.members.some((m) => m.existing));
  const createGroups = groups.filter((g) => !g.members.some((m) => m.existing));

  // ── Create one job per group / update matched jobs ───────
  async function handleCreateAll() {
    if (creating || !groups.length) return;
    const mixed = createGroups.filter((g) => buildGroupJob(g.members).mixedDirection);
    if (mixed.length) {
      setError("A merged group mixes inbound and outbound cards — fix the direction on its cards (or split the group) before creating.");
      return;
    }
    setCreating(true);
    setError("");
    let done = 0;
    try {
      // Signed re-uploads first: advance the matched job's status from the
      // signatures and replace its POD with the signed copy. Direction,
      // stops, and packages are left alone — the job already has them.
      for (const g of updateGroups) {
        const m = g.members.find((x) => x.existing);
        done++;
        setCreateProgress(`Updating ${m.existing.jobNumber || "job"} (${done} of ${groups.length})…`);
        const target = statusAdvanceFromSignatures(m.existing.status, m.signed);
        const patch = {};
        if (target) {
          patch.status = target;
          if (target === "complete" && !m.existing.completedAt) {
            patch.completedAt = new Date().toISOString();
          }
        }
        const img = images.find((i) => i.id === m.imageId);
        if (img) {
          try {
            // The signed copy supersedes the POD on file — overwrite. Read
            // the job fresh: two PODs for one combined job are applied one
            // after the other, and each must keep the other's photo.
            // eslint-disable-next-line no-await-in-loop
            const url = await uploadGuardPhoto(img.file, m.existing.id, "package-doc", userProfile.id || "owner", "pod");
            // eslint-disable-next-line no-await-in-loop
            const fresh = (await getJob(m.existing.id)) || m.existing;
            const norm = (x) => String(x || "").trim().toUpperCase();
            const mine = (fresh.packages || []).filter((p) => m.trackingNumber && norm(p.hawbNumber) === norm(m.trackingNumber));
            if (mine.length) {
              // Only this order's package(s), and the stop they belong to.
              const ids = new Set(mine.map((p) => p.id));
              const stopIds = new Set(mine.map((p) => p.stopId).filter(Boolean));
              patch.packages = fresh.packages.map((p) => (ids.has(p.id) ? { ...p, podPhotoUrl: url } : p));
              patch.stops = (fresh.stops || []).map((s) =>
                s.type !== "airport" && (stopIds.has(s.id) || !stopIds.size) ? { ...s, podPhotoUrl: url } : s);
            } else {
              // No package carries this order number: older one-order jobs.
              patch.stops = (fresh.stops || []).map((s) =>
                s.type === "airport" ? s : { ...s, podPhotoUrl: url });
              if ((fresh.packages || []).length > 0) {
                patch.packages = fresh.packages.map((p) => ({ ...p, podPhotoUrl: url }));
              }
            }
          } catch (err) {
            console.warn("Couldn't upload signed POD for", m.existing.jobNumber, err);
          }
        }
        if (Object.keys(patch).length > 0) {
          // eslint-disable-next-line no-await-in-loop
          await updateJob(m.existing.id, patch);
        }
        // eslint-disable-next-line no-await-in-loop
        await logAudit({
          jobId: m.existing.id, companyId,
          action: `Signed Ferrari copy re-uploaded (bulk) · ${m.existing.jobNumber || ""}${target ? ` · status → ${target}` : ""}`,
          userId: userProfile.id, userName: userProfile.name,
        });
        // The signed copy's contact (delivery-confirmation name, second
        // phone) flows into the location directory too — fills blanks /
        // appends new numbers, never overwrites saved contacts.
        if (m.stop && m.stop.locationName) {
          upsertLocationFromStop(companyId, m.stop, {}).catch((err) => {
            console.warn("Couldn't upsert location for re-upload stop", m.stop, err);
          });
        }
      }
      for (const g of createGroups) {
        done++;
        setCreateProgress(`Creating job ${done} of ${groups.length}…`);
        const job = buildGroupJob(g.members);
        const bornComplete = markComplete;
        const cleanPackages = job.packages.map(({ _cardId, ...p }) => p);
        // eslint-disable-next-line no-await-in-loop
        const ref = await createJob({
          jobNumber: job.jobNumber,
          jobType: job.jobType,
          brokerId: ferrariBroker?.id || "",
          brokerName: ferrariBroker?.name || "Ferrari",
          dispatcherName: ferrariBroker?.dispatcherName || ferrariBroker?.defaultDispatcher || "",
          salesRepName: "",
          salesRepPhone: "",
          bundleId: "",
          airportCode: companyDoc?.hubAirport || "CLT",
          scheduledDate: job.scheduledDate,
          scheduledTime: job.scheduledTime,
          stops: job.stops,
          packages: cleanPackages,
          notes: job.notes,
          dispatcherEmailRaw: "",
          guardAssignments: [],
          guardIds: [],
          companyId,
          numGuards: job.numGuards,
          status: bornComplete ? "complete" : "pool",
          ...(bornComplete ? { completedAt: new Date().toISOString() } : {}),
          createdBy: userProfile.id,
        });

        // Upload each member's form image and stamp it as the POD on that
        // card's package row(s) + its stop. Non-fatal — the job exists
        // either way and the photo can be re-added at billing time.
        try {
          const urlByCard = {};
          for (const m of g.members) {
            const img = images.find((i) => i.id === m.imageId);
            if (!img) continue;
            // eslint-disable-next-line no-await-in-loop
            urlByCard[m.id] = await uploadGuardPhoto(img.file, ref.id, "package-doc", userProfile.id || "owner", "pod");
          }
          const cardIdByStopId = {};
          for (const m of g.members) {
            const pkg = job.packages.find((p) => p._cardId === m.id);
            if (pkg?.stopId && !cardIdByStopId[pkg.stopId]) cardIdByStopId[pkg.stopId] = m.id;
          }
          const stampedStops = job.stops.map((s) => (s.type === "airport" ? s
            : { ...s, podPhotoUrl: urlByCard[cardIdByStopId[s.id]] || Object.values(urlByCard)[0] || null }));
          const stampedPackages = job.packages.map(({ _cardId, ...p }) =>
            ({ ...p, podPhotoUrl: urlByCard[_cardId] || null }));
          // eslint-disable-next-line no-await-in-loop
          await updateJob(ref.id, { stops: stampedStops, packages: stampedPackages });
        } catch (err) {
          console.warn("Couldn't save POD image(s) on job", ref.id, err);
        }

        // eslint-disable-next-line no-await-in-loop
        await logAudit({
          jobId: ref.id, companyId,
          action: `Ferrari POD bulk upload · ${job.jobNumber || "(no #)"} · ${job.scheduledDate || "no date"} · ${job.jobType} · ${g.members.length} POD${g.members.length !== 1 ? "s" : ""}${bornComplete ? " · marked complete" : ""}`,
          userId: userProfile.id, userName: userProfile.name,
        });

        // Directory upkeep: promote stops into saved Locations (dedup +
        // fuzzy parsed-contact merge live in upsertLocationFromStop).
        job.stops.filter((s) => s.type !== "airport").forEach((s) => {
          upsertLocationFromStop(companyId, s, { notes: job.notes }).catch((err) => {
            console.warn("Couldn't upsert location for stop", s, err);
          });
        });
      }
      const parts = [];
      if (createGroups.length) parts.push(`${createGroups.length} Ferrari job${createGroups.length !== 1 ? "s" : ""} created`);
      if (updateGroups.length) parts.push(`${updateGroups.length} updated from signed cop${updateGroups.length !== 1 ? "ies" : "y"}`);
      onCreated?.(parts.join(", ") || "Done");
    } catch (err) {
      console.error("Bulk POD create failed", err);
      setError(`Failed after ${done - 1} of ${groups.length} jobs: ${err?.message || err}`);
      setCreating(false);
      setCreateProgress("");
    }
  }

  // Default the "mark complete" toggle from the parsed dates: PODs are
  // usually uploaded AFTER the run, so if every dated card is today or
  // earlier the trips already happened. Re-derived as parses land.
  const doneCards = cards.filter((c) => c.status === "done");
  useEffect(() => {
    if (!doneCards.length) return;
    const dated = doneCards.filter((c) => c.scheduledDate);
    setMarkComplete(dated.length > 0 && dated.every((c) => c.scheduledDate <= todayYmd()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doneCards.length]);

  const parsingCount = cards.filter((c) => c.status === "parsing").length;
  const errorCards = cards.filter((c) => c.status === "error");

  // ── Render ───────────────────────────────────────────────
  return (
    <div className="modal-overlay" style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
      zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }}>
      {/* Full-size scan. Sits above the batch (not instead of it) so the
          cards are still there when it closes — click anywhere, or Escape. */}
      {zoomImage && (
        <div
          onClick={() => setZoomImage(null)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 400,
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            padding: 20, cursor: "zoom-out",
          }}>
          <div style={{
            color: "#fff", fontSize: 12, marginBottom: 10, display: "flex",
            alignItems: "center", gap: 12, maxWidth: "90vw",
          }}>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {zoomImage.name || "POD form"}
            </span>
            <button type="button"
              onClick={(e) => { e.stopPropagation(); setZoomImage(null); }}
              style={{
                background: "none", border: "1px solid #888", color: "#fff",
                borderRadius: 6, padding: "2px 8px", fontSize: 11, cursor: "pointer",
              }}>✕ Close</button>
          </div>
          <img
            src={zoomImage.preview}
            alt={zoomImage.name || "POD form"}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "90vw", maxHeight: "82vh", objectFit: "contain",
              borderRadius: 6, background: "#fff", cursor: "default",
            }} />
        </div>
      )}
      <div className="modal-card" style={{
        background: "#fff", borderRadius: 12, width: "100%", maxWidth: 860,
        maxHeight: "92vh", overflow: "hidden", display: "flex", flexDirection: "column",
        boxShadow: "0 8px 40px rgba(0,0,0,0.22)",
      }}>
        {/* Header */}
        <div style={{
          padding: "14px 20px", borderBottom: "1px solid #e8e8e8",
          display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0,
        }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>Bulk-upload Ferrari PODs</div>
            <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
              {step === 1
                ? "Add every POD/HAWB form image, then parse them all at once"
                : "Group forms that belong to the same job, then create"}
            </div>
          </div>
          <button onClick={() => { if (!creating) onClose?.(); }}
            style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#888", lineHeight: 1, padding: "2px 6px" }}>
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "18px 22px" }}>
          {step === 1 && (
            <div
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; setDragOver(true); }}
              onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false); }}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer?.files); }}
              style={{
                display: "flex", flexDirection: "column", gap: 14,
                outline: dragOver ? "2px dashed #185FA5" : "2px dashed transparent",
                outlineOffset: 4, borderRadius: 8,
                background: dragOver ? "#F0F7FF" : "transparent", transition: "background 0.15s",
              }}
            >
              <div style={{
                background: "#E6F1FB", borderLeft: "3px solid #185FA5",
                padding: "9px 13px", borderRadius: "0 7px 7px 0", fontSize: 11, color: "#0C3976", lineHeight: 1.65,
              }}>
                Drop or attach <strong>up to {MAX_IMAGES} Ferrari POD/HAWB form photos</strong>.
                Each form is parsed on its own (one AI call per image — that's what keeps the
                fields from bleeding across forms), then you group the ones that belong to the
                same job before creating.
              </div>

              {images.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {images.map((img) => (
                    <div key={img.id} style={{ position: "relative", border: "1px solid #ddd", borderRadius: 6, padding: 3, background: "#f8f9fb" }}>
                      <img src={img.preview} alt={img.name || "POD form"}
                        style={{ display: "block", maxWidth: 130, maxHeight: 110, objectFit: "contain", borderRadius: 4 }} />
                      <button onClick={() => removeImage(img.id)} title="Remove image"
                        style={{
                          position: "absolute", top: -6, right: -6, width: 20, height: 20,
                          borderRadius: "50%", border: "none", background: "#a32d2d", color: "#fff",
                          fontSize: 11, fontWeight: 700, cursor: "pointer", lineHeight: "20px", padding: 0,
                        }}>×</button>
                    </div>
                  ))}
                </div>
              )}

              <input ref={fileInputRef} type="file" accept="image/*" multiple style={{ display: "none" }}
                onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
              <button onClick={() => fileInputRef.current?.click()}
                style={{
                  alignSelf: "flex-start", background: "none", border: "1px dashed #bbb",
                  color: "#555", fontSize: 11, cursor: "pointer", padding: "6px 12px", borderRadius: 6,
                }}>
                📎 Attach POD images{images.length > 0 ? ` (${images.length} added)` : ""}
              </button>

              <div>
                <button className="btn btn-blue" onClick={handleParseAll} disabled={parsing || images.length === 0}>
                  {parsing ? "Parsing…" : `✦ Parse ${images.length || ""} form${images.length !== 1 ? "s" : ""}`}
                </button>
              </div>
              {error && (
                <div style={{ background: "#FDECEA", border: "1px solid #F5C6C0", borderRadius: 8, padding: "9px 13px", fontSize: 11, color: "#8a2c20" }}>
                  {error}
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {parsingCount > 0 && (
                <div style={{ fontSize: 12, color: "#185FA5", fontWeight: 600 }}>
                  ⏳ Parsing {parsingCount} of {cards.length} forms…
                </div>
              )}
              {!ferrariBroker && brokers.length > 0 && (
                <div style={{ background: "#fff3cd", borderLeft: "3px solid #EF9F27", padding: "8px 12px", borderRadius: "0 7px 7px 0", fontSize: 11, color: "#633806" }}>
                  No Ferrari rate card found — jobs will be created with broker "Ferrari" but no
                  rate card link. Add one under Settings → Rate cards for clean billing.
                </div>
              )}
              <div style={{ fontSize: 11, color: "#555", lineHeight: 1.6 }}>
                Each card starts as its own job. <strong>Click cards to select</strong> them and hit
                "Merge selected", or <strong>drag a card onto another group</strong>, when several
                PODs belong to one shipment. ⏏ pops a card back out.
              </div>

              {selected.size >= 2 && (
                <div>
                  <button className="btn btn-sm btn-blue" onClick={mergeSelected}>
                    ⛓ Merge {selected.size} selected into one job
                  </button>
                </div>
              )}

              {/* Groups */}
              {groups.map((g, gi) => {
                const preview = buildGroupJob(g.members);
                const updateTarget = g.members.find((m) => m.existing)?.existing || null;
                return (
                  <div key={g.groupId}
                    onDragOver={(e) => { if (dragCardId.current) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; } }}
                    onDrop={(e) => {
                      e.preventDefault();
                      const dragged = cards.find((c) => c.id === dragCardId.current);
                      // Update-cards stay solo — they patch an existing job,
                      // so nothing can merge into or out of them.
                      if (dragged && !dragged.existing && !g.members.some((m) => m.existing)) {
                        moveCardToGroup(dragged.id, g.groupId);
                      }
                      dragCardId.current = null;
                    }}
                    style={{
                      border: g.members.length > 1 ? "2px solid #185FA5" : "1px solid #e0e0e0",
                      borderRadius: 10, padding: "10px 12px", background: g.members.length > 1 ? "#F7FAFF" : "#fff",
                    }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, fontSize: 12 }}>
                      {updateTarget ? (
                        <>
                          <span style={{ fontWeight: 700, color: "#a36b1d" }}>
                            ↻ Updates {updateTarget.jobNumber || "existing job"}
                          </span>
                          <span style={{ color: "#888" }}>
                            · already created{updateTarget.status ? ` (${updateTarget.status})` : ""} — this signed copy
                            {statusAdvanceFromSignatures(updateTarget.status, g.members[0].signed)
                              ? ` marks it ${statusAdvanceFromSignatures(updateTarget.status, g.members[0].signed) === "complete" ? "complete" : "in progress"} and`
                              : ""} replaces the POD
                          </span>
                        </>
                      ) : (
                        <>
                          <span style={{ fontWeight: 700, color: "#185FA5" }}>
                            Job {gi + 1}
                          </span>
                          <span style={{ color: "#888" }}>
                            · {g.members.length} POD{g.members.length !== 1 ? "s" : ""}
                            {preview.jobNumber ? ` · ${preview.jobNumber}` : ""}
                            {preview.scheduledDate ? ` · ${preview.scheduledDate}` : ""}
                            {` · ${preview.numGuards} guard${preview.numGuards !== 1 ? "s" : ""}`}
                          </span>
                        </>
                      )}
                      {preview.mixedDirection && !updateTarget && (
                        <span style={{ color: "#a32d2d", fontWeight: 700 }}>⚠ mixed directions</span>
                      )}
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                      {g.members.map((card) => (
                        <PodCard key={card.id}
                          card={card}
                          image={images.find((i) => i.id === card.imageId)}
                          selected={selected.has(card.id)}
                          inMultiGroup={g.members.length > 1}
                          onToggleSelect={() => toggleSelect(card.id)}
                          onPopOut={() => popOut(card.id)}
                          onRemove={() => removeCard(card.id)}
                          onZoom={setZoomImage}
                          locations={locations}
                          onUpdate={(patch) => updateCard(card.id, patch)}
                          onDragStart={() => { dragCardId.current = card.id; }}
                          onDragEnd={() => { dragCardId.current = null; }}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}

              {/* Failed parses */}
              {errorCards.map((card) => {
                const img = images.find((i) => i.id === card.imageId);
                return (
                  <div key={card.id} style={{
                    border: "1px solid #F5C6C0", background: "#FDECEA", borderRadius: 10,
                    padding: "10px 12px", display: "flex", alignItems: "center", gap: 10,
                  }}>
                    {img?.preview && (
                      <img src={img.preview} alt={img.name || "POD form"}
                        style={{ maxWidth: 60, maxHeight: 60, objectFit: "contain", borderRadius: 4 }} />
                    )}
                    <div style={{ flex: 1, fontSize: 11, color: "#8a2c20" }}>
                      <strong>Parse failed</strong>{img?.name ? ` — ${img.name}` : ""}: {card.error}
                    </div>
                    <button className="btn btn-sm" onClick={() => retryCard(card.id)}>↻ Retry</button>
                    <button className="btn btn-sm" onClick={() => removeCard(card.id)}
                      style={{ color: "#a32d2d", borderColor: "#a32d2d" }}>Remove</button>
                  </div>
                );
              })}

              {error && (
                <div style={{ background: "#FDECEA", border: "1px solid #F5C6C0", borderRadius: 8, padding: "9px 13px", fontSize: 11, color: "#8a2c20" }}>
                  {error}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {step === 2 && (
          <div style={{
            padding: "12px 20px", borderTop: "1px solid #e8e8e8", flexShrink: 0,
            display: "flex", alignItems: "center", gap: 14,
          }}>
            <button className="btn" onClick={() => { setStep(1); setCards([]); setSelected(new Set()); setError(""); }}
              disabled={parsing || creating}>
              ← Back
            </button>
            <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: "#1d5c1d", cursor: "pointer", flex: 1 }}>
              <input type="checkbox" checked={markComplete} disabled={creating}
                onChange={(e) => setMarkComplete(e.target.checked)} />
              <span>Trips already ran — mark <strong>complete</strong> (→ Ready to bill). POD images are saved on the jobs either way.</span>
            </label>
            <button className="btn btn-blue" onClick={handleCreateAll}
              disabled={creating || parsing || groups.length === 0}>
              {creating ? (createProgress || "Creating…")
                : updateGroups.length === 0
                  ? `Create ${createGroups.length} job${createGroups.length !== 1 ? "s" : ""}`
                  : createGroups.length === 0
                    ? `Update ${updateGroups.length} job${updateGroups.length !== 1 ? "s" : ""}`
                    : `Create ${createGroups.length} · update ${updateGroups.length}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// One parsed POD form. Click = select for merge; drag = move between groups.
function PodCard({ card, image, selected, inMultiGroup, onToggleSelect, onPopOut, onRemove, onZoom, locations = [], onUpdate, onDragStart, onDragEnd }) {
  // The parser reads the address off the form, and on a bad scan it reads
  // the wrong block entirely. Correcting it here beats creating the job and
  // fixing it afterwards, so the stop block opens into an editor.
  const [editingStop, setEditingStop] = useState(false);
  const [editingPod, setEditingPod] = useState(false);
  const patchStop = (patch) => onUpdate({ stop: { ...card.stop, ...patch } });
  const dateWarning = scheduledDateWarning(card.scheduledDate, todayYmd());
  const stopField = {
    width: "100%", fontSize: 10, padding: "3px 5px",
    border: "1px solid #ddd", borderRadius: 5, boxSizing: "border-box",
  };
  if (card.status === "parsing") {
    return (
      <div style={{
        width: 230, border: "1px dashed #bbb", borderRadius: 8, padding: 10,
        display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: "#888",
      }}>
        {image?.preview && (
          <img src={image.preview} alt="" style={{ maxWidth: 48, maxHeight: 48, objectFit: "contain", borderRadius: 4, opacity: 0.5 }} />
        )}
        ⏳ Parsing…
      </div>
    );
  }
  const dirChip = card.jobType === "inbound"
    ? { label: "Inbound", bg: "#E6F1FB", fg: "#185FA5" }
    : { label: "Outbound", bg: "#EDF7ED", fg: "#0a7a4a" };
  return (
    <div
      draggable={!card.existing}
      onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; onDragStart(); }}
      onDragEnd={onDragEnd}
      onClick={onToggleSelect}
      style={{
        width: 230,
        border: selected ? "2px solid #185FA5" : card.existing ? "1px solid #f0d878" : "1px solid #ddd",
        borderRadius: 8, padding: 9,
        background: selected ? "#F0F7FF" : card.existing ? "#fffbe9" : "#fff",
        cursor: card.existing ? "default" : "pointer", fontSize: 11, position: "relative",
        display: "flex", flexDirection: "column", gap: 6,
      }}
    >
      {inMultiGroup && (
        <button title="Pop this card back out into its own job"
          onClick={(e) => { e.stopPropagation(); onPopOut(); }}
          style={{
            position: "absolute", top: 4, right: 30, background: "none",
            border: "1px solid #ccc", borderRadius: 5, fontSize: 10, cursor: "pointer",
            color: "#555", padding: "1px 5px",
          }}>⏏</button>
      )}
      {/* Drop a card from the batch — the same form uploaded twice, or a
          POD that belongs to a month already billed. Until now Remove only
          existed on cards that FAILED to parse, so a duplicate that parsed
          cleanly could only be dealt with by creating the job and
          cancelling it afterwards. Confirms first: the parse costs an API
          call per image, so an accidental click isn't free to undo. */}
      <button title="Remove this card from the batch"
        onClick={(e) => {
          e.stopPropagation();
          const label = card.trackingNumber || card.stop?.locationName || "this card";
          if (window.confirm(`Remove ${label} from this batch? The image stays uploaded — re-parsing it means another pass through the reader.`)) onRemove();
        }}
        style={{
          position: "absolute", top: 4, right: 4, background: "none",
          border: "1px solid #e0b4b4", borderRadius: 5, fontSize: 10, cursor: "pointer",
          color: "#a32d2d", padding: "1px 5px",
        }}>✕</button>
      <div style={{ display: "flex", gap: 8 }}>
        {image?.preview && (
          <img src={image.preview} alt={image.name || "POD form"}
            title="Click to see the full scan"
            onClick={(e) => { e.stopPropagation(); onZoom?.(image); }}
            style={{
              width: 56, height: 56, objectFit: "cover", borderRadius: 4, flexShrink: 0,
              cursor: "zoom-in", border: "1px solid #dde3ec",
            }} />
        )}
        <div style={{ minWidth: 0, flex: 1 }}>
          {/* POD / Order # — editable, because a bad scan loses it and a
              job created without one is unidentifiable to Ferrari. Editing
              it also rewrites the package rows that were only carrying it
              as a fallback (a blank HAWB inherits the form's number at
              parse time), so the two can't drift apart. */}
          {editingPod ? (
            <input
              autoFocus
              value={card.trackingNumber || ""}
              placeholder="POD / Order #"
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => {
                const next = e.target.value.trim();
                const was = card.trackingNumber || "";
                onUpdate({
                  trackingNumber: next,
                  packages: (card.packages || []).map((pkg) => (
                    (!pkg.hawbNumber || pkg.hawbNumber === was)
                      ? { ...pkg, hawbNumber: next }
                      : pkg
                  )),
                });
              }}
              onBlur={() => setEditingPod(false)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === "Escape") setEditingPod(false); }}
              style={{
                width: "100%", fontSize: 11, fontWeight: 700, color: "#185FA5",
                padding: "2px 4px", border: "1px solid #185FA5", borderRadius: 5,
                boxSizing: "border-box",
              }} />
          ) : (
            <div
              onClick={(e) => { e.stopPropagation(); setEditingPod(true); }}
              title={card.trackingNumber ? "Click to edit the POD / Order #" : "No number was read off this form — click to type it"}
              style={{
                fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                cursor: "text", paddingRight: 24,
                color: card.trackingNumber ? "#185FA5" : "#a32d2d",
              }}>
              {card.trackingNumber || "✏️ no POD # — add"}
            </div>
          )}
          <div style={{ display: "flex", gap: 4, alignItems: "center", marginTop: 3, flexWrap: "wrap" }}>
            {card.existing ? (
              // Update card: direction is never touched on a re-upload, so
              // no toggle — just say what this card does.
              <span style={{
                fontSize: 10, fontWeight: 700, padding: "1px 6px",
                background: "#fff7e0", color: "#a36b1d", border: "1px solid #f0d878",
                borderRadius: 6,
              }}>↻ updates existing</span>
            ) : (
              <select value={card.jobType}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => onUpdate({ jobType: e.target.value })}
                style={{
                  fontSize: 10, fontWeight: 700, padding: "1px 3px",
                  background: dirChip.bg, color: dirChip.fg, border: "1px solid " + dirChip.fg + "33",
                  borderRadius: 6,
                }}>
                <option value="inbound">Inbound</option>
                <option value="outbound">Outbound</option>
              </select>
            )}
            {(card.signed.deliverySigned || card.signed.shipperSigned) && (
              <span title={card.signed.deliverySigned ? "Delivery confirmation signed" : "Shipper signed"}
                style={{ fontSize: 10, color: "#0a7a4a", fontWeight: 700 }}>✍ signed</span>
            )}
          </div>
          {card.needsReview && !card.existing && (
            <div style={{ fontSize: 9, color: "#a32d2d", fontWeight: 700, marginTop: 2 }}>
              🧭 confirm direction
            </div>
          )}
        </div>
      </div>

      {/* Same place, or a new one? Shown when the reader's address is close
          to somewhere already on file but not identical. Linking reuses the
          saved coordinates and contacts; keeping it separate saves a new
          location on create. */}
      {card.stop?.suggestedMatch && !card.stop.locationId && (
        <div onClick={(e) => e.stopPropagation()} style={{
          background: "#fff7e6", border: "1px solid #f0c46a", borderRadius: 7,
          padding: "7px 9px", fontSize: 10, color: "#8a5a00",
          display: "flex", flexDirection: "column", gap: 6,
        }}>
          <div>
            ⚠️ Looks like saved <strong>{card.stop.suggestedMatch.name}</strong>
            {card.stop.suggestedMatch.reason ? ` (${card.stop.suggestedMatch.reason})` : ""}. Same place?
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button type="button" className="btn btn-sm btn-blue" style={{ flex: 1, fontSize: 10 }}
              onClick={() => {
                const loc = locations.find((l) => l.id === card.stop.suggestedMatch.id);
                patchStop(loc ? {
                  locationId: loc.id,
                  locationName: loc.name || card.stop.locationName || "",
                  address: loc.address || card.stop.address || "",
                  lat: typeof loc.lat === "number" ? loc.lat : card.stop.lat ?? null,
                  lng: typeof loc.lng === "number" ? loc.lng : card.stop.lng ?? null,
                  suggestedMatch: null,
                } : { suggestedMatch: null });
              }}>Link to it</button>
            <button type="button" className="btn btn-sm" style={{ flex: 1, fontSize: 10 }}
              onClick={() => patchStop({ suggestedMatch: null })}>Keep separate</button>
          </div>
        </div>
      )}

      {/* My local stop — delivery on inbound, pickup on outbound */}
      {card.stop ? (
        <div style={{ background: "#f8f9fb", borderRadius: 6, padding: "5px 7px", lineHeight: 1.45 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 4 }}>
            <div style={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
              {card.jobType === "inbound" ? "📥 Deliver to" : "📤 Pick up at"}{" "}
              {card.stop.locationName || "—"}
              {card.stop.locationId && <span title="Matched a saved location" style={{ color: "#0a7a4a" }}> ✓</span>}
            </div>
            <button type="button"
              title={editingStop ? "Done editing" : "Fix the address the form parsed to"}
              onClick={(e) => { e.stopPropagation(); setEditingStop((v) => !v); }}
              style={{
                background: "none", border: "none", cursor: "pointer", padding: 0,
                fontSize: 10, color: "#185FA5", textDecoration: "underline", flexShrink: 0,
              }}>{editingStop ? "done" : "✏️ edit"}</button>
          </div>
          {editingStop ? (
            <div onClick={(e) => e.stopPropagation()}
              style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 4 }}>
              <input value={card.stop.locationName || ""} placeholder="Location name"
                onChange={(e) => patchStop({ locationName: e.target.value })}
                style={stopField} />
              {/* Places, not a plain box: a typed address has no pin, and
                  without a pin the mileage auto-calc has nothing to measure.
                  Picking from the dropdown also detaches the card from the
                  saved location it originally matched — it's a different
                  place now, so it gets saved as a new one. */}
              <PlacesAutocomplete
                value={card.stop.address || ""}
                placeholder="Search the correct address…"
                onChange={(address, lat, lng, meta) => patchStop({
                  address,
                  lat: typeof lat === "number" ? lat : null,
                  lng: typeof lng === "number" ? lng : null,
                  placeId: meta?.placeId || null,
                  locationId: null,
                })}
                style={stopField} />
              {card.stop.address && typeof card.stop.lat !== "number" && (
                <div style={{ fontSize: 9, color: "#a36b1d" }}>
                  ⚠ No map pin — pick from the dropdown, or set it later on the job.
                </div>
              )}
              <div style={{ display: "flex", gap: 4 }}>
                <input value={card.stop.contactName || ""} placeholder="Contact"
                  onChange={(e) => patchStop({ contactName: e.target.value })}
                  style={stopField} />
                <input value={card.stop.contactPhone || ""} placeholder="Phone"
                  onChange={(e) => patchStop({ contactPhone: e.target.value })}
                  style={stopField} />
              </div>
            </div>
          ) : (
            <>
              {card.stop.address && <div style={{ color: "#666", fontSize: 10 }}>{card.stop.address}</div>}
              {(card.stop.contactName || card.stop.contactPhone) && (
                <div style={{ color: "#555", fontSize: 10, marginTop: 2 }}>
                  👤 {[card.stop.contactName, card.stop.contactPhone].filter(Boolean).join(" · ")}
                  {card.contactOnFile
                    ? <span style={{ color: "#0a7a4a" }}> · on file</span>
                    : card.stop.locationId
                      ? <span style={{ color: "#a36b1d" }}> · new — will be saved</span>
                      : null}
                </div>
              )}
            </>
          )}
        </div>
      ) : null}

      {/* Form checks — wrong document type, number disagreements, missing
          stop. Amber, advisory; the owner decides whether to proceed. */}
      {card.warnings?.length > 0 && (
        <div style={{
          background: "#FFF7E0", border: "1px solid #F0D88C", borderRadius: 6,
          padding: "5px 7px", color: "#74540C", fontSize: 10, lineHeight: 1.5,
        }}>
          {card.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
        </div>
      )}

      {/* Date check — a misread year ("26" read as "28") would otherwise
          land on the job unnoticed. Advisory; recomputed as the owner edits. */}
      {dateWarning && (
        <div style={{
          background: "#FFF7E0", border: "1px solid #F0D88C", borderRadius: 6,
          padding: "5px 7px", color: "#74540C", fontSize: 10, lineHeight: 1.5,
        }}>
          ⚠ {dateWarning.message}
          {dateWarning.suggestedDate && (
            <button type="button"
              onClick={(e) => { e.stopPropagation(); onUpdate({ scheduledDate: dateWarning.suggestedDate }); }}
              style={{ marginLeft: 6, fontSize: 10, padding: "1px 6px", border: "1px solid #D9B85C", borderRadius: 5, background: "#fff", color: "#74540C", cursor: "pointer" }}>
              Use {dateWarning.suggestedDate}
            </button>
          )}
        </div>
      )}

      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
        <input type="date" value={card.scheduledDate || ""}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onUpdate({ scheduledDate: e.target.value })}
          style={{ fontSize: 10, padding: "2px 4px", border: "1px solid #ddd", borderRadius: 5 }} />
        {/* Weight and guard count both reach the monthly Ferrari bill —
            weight has its own column, guards drive the hourly lines — so
            neither can be display-only when the scan misreads them. Weight
            lives on the first package, which is where the bill reads it. */}
        <span style={{ display: "flex", alignItems: "center", gap: 2 }}
          onClick={(e) => e.stopPropagation()}
          title="Weight on the form — shows in the Weight column of the monthly bill">
          <span style={{ color: "#666", fontSize: 10 }}>⚖</span>
          <input
            value={card.packages?.[0]?.weight || ""}
            placeholder="wt"
            onChange={(e) => onUpdate({
              packages: (card.packages?.length ? card.packages : [{ hawbNumber: card.trackingNumber || "" }])
                .map((pkg, i) => (i === 0 ? { ...pkg, weight: e.target.value } : pkg)),
            })}
            style={{ width: 52, fontSize: 10, padding: "2px 3px", border: "1px solid #ddd", borderRadius: 5 }} />
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 2 }}
          onClick={(e) => e.stopPropagation()}
          title="Guards on this run. Ferrari's 100-lb rule makes it 2 — override if the field went differently.">
          <span style={{ color: card.guards > 1 ? "#a36b1d" : "#666", fontSize: 10 }}>👥</span>
          <select
            value={card.guards || 1}
            onChange={(e) => onUpdate({ guards: Number(e.target.value) })}
            style={{
              fontSize: 10, padding: "1px 2px", borderRadius: 5,
              border: "1px solid " + (card.guards > 1 ? "#f0c878" : "#ddd"),
              color: card.guards > 1 ? "#a36b1d" : "#555",
              fontWeight: card.guards > 1 ? 700 : 400,
            }}>
            {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </span>
      </div>
    </div>
  );
}
